import axios from 'axios'

// Helper function to get CSRF token from cookie
function getCsrfToken() {
  const name = 'csrf_token='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
})

// Add CSRF token to all requests
api.interceptors.request.use(
  (config) => {
    const csrfToken = getCsrfToken()
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Auth endpoints
export const authAPI = {
  login: (email, password, rememberMe = false) => {
    return api.post('/auth/login', { email, password, remember_me: rememberMe })
  },
  logout: () => {
    return api.post('/auth/logout')
  },
  me: () => {
    return api.get('/auth/me')
  },
  changePasswordAdmin: (oldPassword, newPassword) => {
    return api.post('/auth/admin/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    })
  },
  changePasswordCustomer: (oldPassword, newPassword) => {
    return api.post('/auth/customer/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    })
  },
}

// Apps endpoints
export const appsAPI = {
  getAll: () => api.get('/apps/'),
  getById: (id) => api.get(`/apps/${id}`),
  getBySlug: (slug) => api.get(`/apps/slug/${slug}`),
  create: (data) => api.post('/apps/', data),
  update: (id, data) => api.put(`/apps/${id}`, data),
  delete: (id) => api.delete(`/apps/${id}`),
}

// App system email settings (pushed to the app via webhook)
export const appSystemSettingsAPI = {
  get: (appId) => api.get(`/apps/${appId}/system-email-settings`),
  update: (appId, data) => api.put(`/apps/${appId}/system-email-settings`, data),
}

// Customers endpoints
export const customersAPI = {
  getAll: () => api.get('/customers/'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers/', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
}

// Subscriptions endpoints
export const subscriptionsAPI = {
  getAllAdmin: () => api.get('/subscriptions/admin/all'),
  getMy: () => api.get('/subscriptions/my-subscriptions'),
  create: (data) => api.post('/subscriptions/', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  requestCancellation: (id) => api.post(`/subscriptions/${id}/request-cancellation`),
  toggleAutoRenew: (id) => api.post(`/subscriptions/${id}/toggle-auto-renew`),
}

// Pages endpoints
export const pagesAPI = {
  getAll: () => api.get('/pages/'),
  getAllAdmin: () => api.get('/pages/all'),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
  create: (data) => api.post('/pages/', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  clone: (id, newTitle, newSlug) => api.post(`/pages/${id}/clone`, { new_title: newTitle, new_slug: newSlug }),
  delete: (id) => api.delete(`/pages/${id}`),
}

// Contact endpoints
export const contactAPI = {
  send: (data) => api.post('/contact/', data),
  getAll: () => api.get('/contact/'),
  update: (id, data) => api.put(`/contact/${id}`, data),
  delete: (id) => api.delete(`/contact/${id}`),
}

// Settings endpoints
export const settingsAPI = {
  getPublic: () => api.get('/settings/public'),
  getAll: () => api.get('/settings/'),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
}

// Menu endpoints
export const menuAPI = {
  getPublic: () => api.get('/menu/public'),
  getAll: () => api.get('/menu/'),
  getById: (id) => api.get(`/menu/${id}`),
  create: (data) => api.post('/menu/', data),
  update: (id, data) => api.put(`/menu/${id}`, data),
  delete: (id) => api.delete(`/menu/${id}`),
}

// Tickets endpoints
export const ticketsAPI = {
  // Customer endpoints
  getMyTickets: () => api.get('/tickets/my-tickets'),
  create: (data) => api.post('/tickets/', data),
  addReply: (ticketId, message) => api.post(`/tickets/${ticketId}/reply`, null, { params: { message } }),

  // Admin endpoints
  getAllAdmin: (status = null) => api.get('/tickets/admin/all', { params: { status } }),
  getByIdAdmin: (id) => api.get(`/tickets/admin/${id}`),
  updateAdmin: (id, data) => api.put(`/tickets/admin/${id}`, data),
  addReplyAdmin: (ticketId, message, isInternal = false) => api.post(`/tickets/admin/${ticketId}/reply`, null, { params: { message, is_internal: isInternal } }),
  deleteAdmin: (id) => api.delete(`/tickets/admin/${id}`),
}

// App Plans endpoints
export const appPlansAPI = {
  // Admin endpoints
  getAll: (appId = null) => api.get('/app-plans/', { params: { app_id: appId } }),
  getById: (id) => api.get(`/app-plans/${id}`),
  create: (data) => api.post('/app-plans/', data),
  update: (id, data) => api.put(`/app-plans/${id}`, data),
  delete: (id) => api.delete(`/app-plans/${id}`),

  // Public endpoint
  getPublicPlans: (appId) => api.get(`/app-plans/public/${appId}`),
}

// Order/Checkout endpoints (für öffentliche Bestellungen)
export const orderAPI = {
  create: (data) => api.post('/orders/', data),
}

// Services endpoints (Admin only - for managing backend services)
export const servicesAPI = {
  getAll: () => api.get('/services/'),
  getStatus: (serviceName) => api.get(`/services/${serviceName}/status`),
  restart: (serviceName) => api.post(`/services/${serviceName}/restart`),
  getLogs: (serviceName, lines = 100) => api.get(`/services/${serviceName}/logs`, { params: { lines } }),
}

// Admin console build endpoints (Admin only - Ausbaustufe A)
export const buildAPI = {
  getApps: () => api.get('/admin/builds/apps'),
  start: (app) => api.post(`/admin/builds/${app}`),
  getRuns: (app) => api.get(`/admin/builds/${app}/runs`),
  getRun: (runId) => api.get(`/admin/builds/runs/${runId}`),
  cancel: (runId) => api.post(`/admin/builds/runs/${runId}/cancel`),
  rollback: (app) => api.post(`/admin/builds/${app}/rollback`),
}

// Admin console migration endpoints (Admin only - Ausbaustufe B)
export const migrationsAPI = {
  getApps: () => api.get('/admin/migrations/apps'),
  upgrade: (app) => api.post(`/admin/migrations/${app}/upgrade`),
  downgrade: (app, target) => api.post(`/admin/migrations/${app}/downgrade`, { target }),
  stamp: (app, target) => api.post(`/admin/migrations/${app}/stamp`, { target }),
  getRuns: (app) => api.get(`/admin/migrations/${app}/runs`),
  getRun: (runId) => api.get(`/admin/migrations/runs/${runId}`),
  cancel: (runId) => api.post(`/admin/migrations/runs/${runId}/cancel`),
}

// Invoices endpoints
export const invoicesAPI = {
  // Admin endpoints
  getAllAdmin: () => api.get('/invoices/admin/all'),
  create: (formData) => api.post('/invoices/admin/create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/invoices/admin/${id}`, data),
  delete: (id) => api.delete(`/invoices/admin/${id}`),
  uploadFile: (id, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/invoices/admin/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  download: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),

  // Customer endpoints
  getMy: () => api.get('/invoices/my-invoices'),
  downloadMy: (id) => api.get(`/invoices/my-invoices/${id}/download`, { responseType: 'blob' }),
}

// Media / Dateimanager endpoints (Admin only)
export const mediaAPI = {
  upload: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  list: () => api.get('/media/'),
  delete: (filename: string) => api.delete(`/media/${filename}`),
}

// Changelog ("Letzte Änderungen & Updates") — sichtbar für Admin & Kunde
export const changelogAPI = {
  getAll: () => api.get('/changelog/'),
}

export default api
